import React, { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"

// styles
import Styles from "./spotWalletTab.module.css"

// icons
import { BiWallet } from "react-icons/bi"
import { RiShareBoxFill } from "react-icons/ri"
import { FiEye, FiEyeOff, FiSearch } from "react-icons/fi"
import { TbArrowUpRight, TbArrowDownLeft } from "react-icons/tb"

// components
import Button from "../../../../components/Button/Button"
import CoinTag from "../../../../components/CoinTag/CoinTag"
import TableButton from "../../../../components/TableButton/TableButton"
import Pagination from "@mui/material/Pagination"

// redux
import { useSelector, useDispatch } from "react-redux"
import { _getCurrencies, _getBalances } from "../../../exchange/redux/actions"
import {
  setSelectedCoinId
} from "../../../../redux/slices/depositSlice"
import {
  setWarningModalData,
  setWarningModalOpen
} from "../../../modals/warning/redux/warningModalSlice"
import {
  setWalletDepositModalData
} from "../../../modals/deposit-withdraw/redux/walletDepositModalSlice"
import {
  setSelectCurrencyModalData,
  setSelectCurrencyModalOpen
} from "../../../modals/select-currency/redux/selectCurrencyModalSlice"

// utils
import { injectIntl } from "react-intl"
import { formatNumber } from "../../../helpers/formatNumber"
import {
  setWithdrawFormModalOpen,
  setwithdrawFormModalData
} from "../../../modals/deposit-withdraw/redux/withdrawFormModalSlice"
import { handleDeposit as handleDepositUtil } from "../../../../utility/deposit-withdraw-helpers/handleDeposit"

function SpotWalletTab() {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  const theme = useSelector((state) => state.theme.value)

  const TotalWithdraws = useSelector(
    (state) => state.fundsData.data.total_withdrawals
  )
  const TotalDeposits = useSelector(
    (state) => state.fundsData.data.total_deposits
  )
  const [selectedTab, setSelectedTab] = useState("all")
  const [testCurrencies, setTestCurrencies] = useState([])
  const [balances, setBalances] = useState([])
  const [filteredBalances, setFilteredBalances] = useState([])
  const [showPrice, setShowPrice] = useState(true)
  const [tableLoading, setTableLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState("")

  // Pagination settings
  const [page, setPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [totalPages, setTotalPages] = useState(1)

  const handleWalletNotFound = () => {
    dispatch(setWarningModalOpen(true))
    dispatch(
      setWarningModalData({
        title: "Wallet Address Not Found",
        subtitle: "There was no wallet found for the selected coin",
        callback: () => dispatch(setWarningModalOpen(false))
      })
    )
  }

  const handleSearch = (e) => {
    const inputValue = e.target.value
    setSearchTerm(inputValue)

    if (inputValue === "" || inputValue === null || inputValue === undefined) {
      // Apply current tab filter to all balances
      let filtered = balances
      if (selectedTab === "available") {
        filtered = balances.filter((balance) => Number(balance.balance) > 0)
      }
      setFilteredBalances(filtered)
      return
    }

    // Filter based on search term and current tab
    let searchFiltered = balances.filter((el) => {
      return (
        el.id.toLowerCase().includes(inputValue.toLowerCase()) ||
        el.name.toLowerCase().includes(inputValue.toLowerCase())
      )
    })

    if (selectedTab === "available") {
      searchFiltered = searchFiltered.filter((balance) => Number(balance.balance) > 0)
    }

    setFilteredBalances(searchFiltered)
  }

  useEffect(() => {
    setTableLoading(true)
    setFilteredBalances([])
    _getCurrencies((currencydata) => {
      setTestCurrencies(currencydata)
      _getBalances(
        (data) => {
          setBalances(() => {
            const newBalances = currencydata.map((currency, index) => {
              return {
                ...currency,
                balance:
                  data.find((wallet) => wallet.currency === currency.id)
                    ?.balance ?? 0.0,
                locked:
                  data.find((wallet) => wallet.currency === currency.id)
                    ?.locked ?? 0.0
              }
            })

            // Apply search and tab filters
            let filtered = newBalances
            if (selectedTab === "available") {
              filtered = newBalances.filter(
                (balance) => Number(balance.balance) > 0
              )
            }

            // Apply search filter if search term exists
            if (searchTerm) {
              filtered = filtered.filter((el) => {
                return (
                  el.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  el.name.toLowerCase().includes(searchTerm.toLowerCase())
                )
              })
            }

            // Calculate total pages
            setTotalPages(Math.ceil(filtered.length / rowsPerPage))

            // Apply pagination to filtered data
            const startIndex = (page - 1) * rowsPerPage
            const paginatedBalances = filtered.slice(startIndex, startIndex + rowsPerPage)

            setFilteredBalances(paginatedBalances)
            return newBalances
          })
          setTableLoading(false)
        },
        (err) => setTableLoading(false)
      )
    })
  }, [selectedTab, page, rowsPerPage, searchTerm])

  useEffect(() => {
    if (location.pathname === "/account/profile/wallet/all") {
      setSelectedTab("all")
    }
    if (location.pathname === "/account/profile/wallet/available") {
      setSelectedTab("available")
    }
  }, [location])

  const handlePageChange = (event, newPage) => {
    setPage(newPage)
  }

  const handleDeposit = (coin_id) => {
    // First set the deposit modal data
    dispatch(
      setWalletDepositModalData({
        title: "",
        qr: null,
        destination: null,
        callback: () => { }
      })
    );

    // Then use the utility function to handle deposit
    handleDepositUtil(coin_id);
  }

  // ----- RESPONSIVENESS STATE ---------
  // ------------------------------------
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 820)

  const handleResize = () => {
    setIsMobileView(window.innerWidth < 820)
  }

  useEffect(() => {
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])
  // ------------------------------------
  // ------------------------------------

  // Calculate total balance
  const totalBalance = balances.reduce((sum, wallet) => {
    const amount = parseFloat(wallet.balance) + parseFloat(wallet.locked)
    return sum + (isNaN(amount) ? 0 : amount)
  }, 0)

  return (
    <div className={Styles.modernWalletContainer}>
      {/* Modern Wallet Header */}
      <div className={Styles.walletHeader}>
        <div className={Styles.headerTop}>
          <div className={Styles.titleSection}>
            <h1 className={Styles.walletTitle}>Spot wallet</h1>
            <p className={Styles.walletSubtitle}>
              All your crypto techs, stored in one place. Cool huh?
            </p>
          </div>

          <div className={Styles.actionButtons}>
            <Button
              text="Deposit"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600"
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: "Select coin to deposit",
                    showTabs: false,
                    type: "coin",
                    searchPlaceholder: "Search coin name",
                    currenciesToHide: [],
                    disableBackdropClick: true,
                    callback: (coin) => {
                      dispatch(setSelectedCoinId(coin.id));
                      handleDepositUtil(coin.id);
                      setSelectCurrencyModalOpen(false);
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
            <Button
              text="Withdraw"
              variant="dark"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                borderRadius: "12px",
                padding: "12px 24px",
                fontSize: "14px",
                fontWeight: "600",
                backgroundColor: "transparent",
                border: `1px solid ${theme === "light" ? "rgba(139, 154, 171, 0.3)" : "rgba(139, 154, 171, 0.3)"}`,
                color: theme === "light" ? "#001e28" : "#ffffff"
              }}
              onClick={() => {
                dispatch(
                  setSelectCurrencyModalData({
                    title: "Select coin",
                    showTabs: false,
                    type: "coin",
                    showMarketPairs: false,
                    currenciesToHide: [],
                    disableBackdropClick: false,
                    callback: (coin) => {
                      dispatch(
                        setwithdrawFormModalData({
                          selectedCoinID: coin.id,
                          callback: () => {
                            // console.log("HERE") // call API here to proceed
                          }
                        })
                      )
                      dispatch(setWithdrawFormModalOpen(true))
                    }
                  })
                )
                dispatch(setSelectCurrencyModalOpen(true))
              }}
            />
          </div>
        </div>

        <div className={Styles.balanceSection}>
          {/* Balance Display */}
          <div className={Styles.balanceDisplay}>
            <div className={Styles.balanceLabel}>
              <BiWallet size={20} />
              <span>Wallet Balance</span>
            </div>

            <div className={Styles.balanceAmount}>
              <h2 className={Styles.balanceValue}>
                {showPrice ? formatNumber(totalBalance) : '••••••••'}
                <span className={Styles.currency}>USD</span>
              </h2>

              <button
                onClick={() => setShowPrice(!showPrice)}
                className={Styles.eyeButton}
              >
                {showPrice ? <FiEyeOff size={16} /> : <FiEye size={16} />}
              </button>
            </div>

            <p className={Styles.btcEquivalent}>
              ≈ {showPrice ? formatNumber(totalBalance / 100000, 8) : '••••••••'} BTC
            </p>
          </div>

          {/* Stats Cards */}
          <div className={Styles.statsCards}>
            <div className={Styles.statCard}>
              <div className={Styles.statHeader}>
                <TbArrowDownLeft size={16} color="var(--success-color)" />
                <span>Total Deposited</span>
              </div>
              <p className={Styles.statValue}>
                {showPrice ? `$${formatNumber(TotalDeposits)}` : '••••••••'}
              </p>
            </div>

            <div className={Styles.statCard}>
              <div className={Styles.statHeader}>
                <TbArrowUpRight size={16} color="var(--danger-color)" />
                <span>Total Withdrawn</span>
              </div>
              <p className={Styles.statValue}>
                {showPrice ? `$${formatNumber(TotalWithdraws)}` : '••••••••'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Wallet Assets Section */}
      <div className={Styles.assetsSection}>
        <div className={Styles.assetsHeader}>
          <h2 className={Styles.assetsTitle}>Spot wallet</h2>

          <div className={Styles.searchContainer}>
            <FiSearch className={Styles.searchIcon} />
            <input
              type="text"
              placeholder="Search wallet"
              value={searchTerm}
              onChange={handleSearch}
              className={Styles.searchInput}
            />
          </div>
        </div>

        {/* Filter Tabs */}
        <div className={Styles.filterTabs}>
          <button
            className={`${Styles.filterTab} ${selectedTab === "all" ? Styles.activeTab : ""}`}
            onClick={() => {
              if (tableLoading) return
              navigate("/account/profile/wallet/all", { replace: true })
            }}
          >
            All
          </button>
          <button
            className={`${Styles.filterTab} ${selectedTab === "available" ? Styles.activeTab : ""}`}
            onClick={() => {
              if (tableLoading) return
              navigate("/account/profile/wallet/available", { replace: true })
            }}
          >
            Available
          </button>
        </div>

        {/* Table Header */}
        <div className={Styles.tableHeader}>
          <span>Coin</span>
          <span>Avbl. Balance</span>
          <span>Locked</span>
          <span>Amount</span>
          <span>Action</span>
        </div>

        {/* Wallet Rows */}
        <div className={Styles.tableBody}>
          {filteredBalances.map((wallet, index) => (
            <div key={wallet.id} className={Styles.tableRow}>
              {/* Coin */}
              <div className={Styles.coinCell}>
                <img src={wallet?.icon_url} alt={wallet?.name} className={Styles.coinIcon} />
                <div className={Styles.coinInfo}>
                  <div className={Styles.coinSymbol}>
                    <span>{wallet?.id?.toUpperCase()}</span>
                    <CoinTag tag={wallet?.id?.toUpperCase()} size={10} />
                  </div>
                  <span className={Styles.coinName}>{wallet?.name}</span>
                </div>
              </div>

              {/* Available Balance */}
              <span className={Styles.balanceCell}>
                {showPrice ? formatNumber(wallet.balance, 8) : '••••••••'}
              </span>

              {/* Locked */}
              <span className={Styles.lockedCell}>
                {showPrice ? formatNumber(wallet.locked, 2) : '••••'}
              </span>

              {/* Amount */}
              <span className={Styles.amountCell}>
                {showPrice ? formatNumber(parseFloat(wallet.balance) + parseFloat(wallet.locked), 2) : '••••••••'}
              </span>

              {/* Actions */}
              <div className={Styles.actionsCell}>
                <TableButton
                  buttonText="Deposit"
                  buttonIcon={<RiShareBoxFill />}
                  onClick={() => {
                    dispatch(
                      setSelectCurrencyModalData({
                        title: "Select coin to deposit",
                        showTabs: false,
                        type: "coin",
                        searchPlaceholder: "Search coin name",
                        currenciesToHide: [],
                        disableBackdropClick: true,
                        callback: (coin) => {
                          dispatch(setSelectedCoinId(coin.id));
                          handleDepositUtil(coin.id);
                          setSelectCurrencyModalOpen(false);
                        }
                      })
                    )
                    dispatch(setSelectCurrencyModalOpen(true))
                  }}
                />
                <TableButton
                  buttonText="Withdraw"
                  buttonIcon={<RiShareBoxFill />}
                  onClick={() => {
                    dispatch(
                      setwithdrawFormModalData({
                        selectedCoinID: wallet.id,
                        callback: () => {
                          // console.log("HERE") // call API here to proceed
                        }
                      })
                    )
                    dispatch(setWithdrawFormModalOpen(true))
                  }}
                />
                <TableButton
                  buttonText="Convert"
                  buttonIcon={<RiShareBoxFill />}
                  onClick={() =>
                    navigate(`/convert/${wallet.id.toUpperCase()}_USD`)
                  }
                />
              </div>
            </div>
          ))}
        </div>

        {filteredBalances.length === 0 && !tableLoading && (
          <div className={Styles.emptyState}>
            <p>No wallets found</p>
            <span>Try adjusting your search or filter criteria</span>
          </div>
        )}

        {/* Pagination */}
        <div className={Styles.paginationWrapper}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            color="primary"
            shape="rounded"
            size={isMobileView ? "small" : "medium"}
          />
        </div>
      </div>
    </div>
  )
}

export default injectIntl(SpotWalletTab)
